import { createEvent } from 'effector';
import { track } from './amplitude';
import {
  HISTORICAL_FEATURE_OPENED,
  HISTORICAL_FEATURE_CLOSED,
  HISTORICAL_SLIDER_SLIDE_CHANGED,
  HISTORICAL_SCENE_SELECTED,
  ERA_SCENE_ENTERED,
} from './events';
import {
  ERA_SCENE_PARENT,
  isEraScene,
  isStubEraScene,
} from '../scene/eraScenes/registry';
import { $historyMode } from '../state/historicalScenes';
import {
  $activeScene,
  enterScene,
  replaceScene,
  rebuildStack,
} from '../state/sceneNavigation';

/**
 * Historical-scenes analytics (§ Historical Scenes in README).
 *
 * `Historical Feature Opened` / `Historical Feature Closed` are BOTH derived
 * from `$historyMode` transitions (closed→open / open→closed), NOT UI-dispatched.
 * Deriving Opened from the store (rather than the clock-button click handler)
 * makes it race-free: the React handler's `useUnit` snapshot is stale on rapid
 * double-clicks and used to double-fire Opened. The clock is the only path that
 * opens the feature, so the transition IS the tap. Closed covers scrim tap,
 * clock toggle-off, era-selection navigation, any other navigation reset, and
 * (best-effort) page unload with the slider open (`outcome: 'abandoned'`).
 *
 * UI-dispatched events:
 *   - `historicalSliderSlideChanged` — a different era card became active
 *     (HistoricalScenesSlider `onSlideChange`; does NOT fire for the initial
 *     card; transit cards during a fast fling DO count as viewed).
 *   - `historicalSceneSelected` — an "Explore" CTA navigated to an era / today.
 *
 * `Era Scene Entered` is state-derived from `enterScene` / `replaceScene` /
 * `rebuildStack` (mirrors `Inside Scene Entered` in navigationTracker) — it
 * catches ALL entry paths: slider Explore, menu tab, deep-link page load,
 * browser back/forward. Click-source stays on the two UI events above plus
 * `Menu Scene Selected (target_kind='era')`.
 *
 * Era ids are intentionally NOT routed through the generic `Scene Entered`
 * funnel (they are sub-scenes of a main scene, not first-class locations).
 */
export const historicalFeatureOpened = createEvent<{ sceneId: string | null }>();

export const eraSceneEntered = createEvent<{ sceneId: string }>();

export const historicalSliderSlideChanged = createEvent<{
  toSceneId: string;
  index: number;
}>();

export const historicalSceneSelected = createEvent<{
  fromSceneId: string | null;
  toSceneId: string;
}>();

const mainSceneOf = (sceneId: string): string =>
  ERA_SCENE_PARENT[sceneId] ?? sceneId;

/** The "today" slide (the main scene itself) — not an era. */
const isTodayScene = (sceneId: string): boolean => !isEraScene(sceneId);

type HistoryEpisode = {
  openedAt: number;
  sceneId: string | null;
  erasViewed: Set<string>;
  selectedKind: 'era' | 'today' | null;
};

export const startHistoricalScenesTracking = () => {
  // Active feature episode (clock open → close). Module-level singleton, like
  // `cameraSessionTracker` — session-scoped by module load.
  let episode: HistoryEpisode | null = null;

  historicalFeatureOpened.watch(({ sceneId }) => {
    episode = {
      openedAt: performance.now(),
      sceneId,
      erasViewed: new Set<string>(),
      selectedKind: null,
    };
    track(HISTORICAL_FEATURE_OPENED, {
      scene_id: sceneId,
      main_scene_id: sceneId != null ? mainSceneOf(sceneId) : null,
    });
  });

  historicalSliderSlideChanged.watch(({ toSceneId, index }) => {
    const toIsToday = isTodayScene(toSceneId);
    // Browse depth counts ERAS only — the "today" card is a return target,
    // not an era.
    if (episode && !toIsToday) episode.erasViewed.add(toSceneId);
    track(HISTORICAL_SLIDER_SLIDE_CHANGED, {
      scene_id: toSceneId,
      main_scene_id: mainSceneOf(toSceneId),
      index,
      to_is_today: toIsToday,
    });
  });

  historicalSceneSelected.watch(({ fromSceneId, toSceneId }) => {
    const toIsToday = isTodayScene(toSceneId);
    if (episode) episode.selectedKind = toIsToday ? 'today' : 'era';
    track(HISTORICAL_SCENE_SELECTED, {
      from_scene_id: fromSceneId,
      to_scene_id: toSceneId,
      main_scene_id: mainSceneOf(toSceneId),
      to_is_today: toIsToday,
      to_is_stub: isStubEraScene(toSceneId),
    });
  });

  // --- Era scene entered (state-based — ловит ВСЕ entries) ---
  // Slider Explore / menu tab → enterScene | replaceScene (sibling era) |
  // rebuildStack (menu from outside the family); deep-link page load and
  // browser back/forward → rebuildStack. goBack never lands ON an era (eras
  // are always the stack top), so it needs no watch.

  eraSceneEntered.watch(({ sceneId }) => {
    track(ERA_SCENE_ENTERED, {
      scene_id: sceneId,
      main_scene_id: mainSceneOf(sceneId),
      is_stub: isStubEraScene(sceneId),
    });
  });

  enterScene.watch(({ sceneId }) => {
    if (isEraScene(sceneId)) eraSceneEntered({ sceneId });
  });
  replaceScene.watch(({ sceneId }) => {
    if (isEraScene(sceneId)) eraSceneEntered({ sceneId });
  });
  rebuildStack.watch((stack) => {
    const top = stack[stack.length - 1];
    if (top && isEraScene(top.sceneId)) eraSceneEntered({ sceneId: top.sceneId });
  });

  // --- Feature opened / closed — derived from `$historyMode` transitions ---

  const closeEpisode = (
    outcome: 'selected' | 'returned_today' | 'dismissed' | 'abandoned',
  ) => {
    if (!episode) return;
    track(HISTORICAL_FEATURE_CLOSED, {
      scene_id: episode.sceneId,
      main_scene_id:
        episode.sceneId != null ? mainSceneOf(episode.sceneId) : null,
      outcome,
      dwell_ms: Math.round(performance.now() - episode.openedAt),
      eras_viewed_count: episode.erasViewed.size,
    });
    episode = null;
  };

  let prevMode = $historyMode.getState();
  $historyMode.watch((mode) => {
    const prev = prevMode;
    prevMode = mode;
    if (prev === 'closed' && mode !== 'closed') {
      // `$activeScene.sceneId` is the URL-id ('kaaba' / 'kaaba-600ce') — same
      // id space as ERA_SCENE_PARENT (NOT `$sceneId`, which is the poiFilterId
      // form: 'main' for kaaba).
      historicalFeatureOpened({
        sceneId: $activeScene.getState()?.sceneId ?? null,
      });
    } else if (prev !== 'closed' && mode === 'closed') {
      closeEpisode(
        episode?.selectedKind === 'era'
          ? 'selected'
          : episode?.selectedKind === 'today'
            ? 'returned_today'
            : 'dismissed',
      );
    }
  });

  // Slider open at page unload → the episode would be lost. Best-effort flush
  // (same `pagehide` pattern as Session Summary); `episode = null` inside
  // closeEpisode makes a bfcache-restore double-fire a no-op.
  window.addEventListener('pagehide', () => closeEpisode('abandoned'));
};
